(ns ballot-scout-v2-frontend.components.field-matcher
  (:require
   [ballot-scout-v2-frontend.components.icon :refer [icon] :as icon]
   [clj-fuzzy.metrics :as fuzzy-metrics]
   [clojure.string :as str]
   [reagent.core :as r]))

(def not-imported "-- do not import --")

(defn required-fields [standard-fields existing-fields]
  (filter #((:required %) existing-fields) standard-fields))

(defn header-match-distance
  "Takes a map `db-fields` of Ballot Scout database fields and their metadata
  (typically the one returned by `database-fields`) and a string `header` from
  a partner import or disposition file.

  Returns a vector of the match distance value (between 0 and 1; 0 being an
  exact match and higher values being fuzzier matches), the passed-in header,
  and the keyword name of the Ballot Scout database field being matched to it.

  The match values are generally jaccard distances, but we have a few peephole
  checks before we run that algorithm on the pairs.
  They are:
  1. If the lower-cased partner header fully contains our lower-cased field
  name as a substring, then we inject that as a match distance of 0.01.
  2. If the lower-cased partner header contains every word of our lower-cased
  field name, then we inject that as a match distance of 0.011.
  3. If the lower-cased partner header contains the word 'zip' and every
  word in our lower-cased field name is contained in it except for 'code'
  (as in 'zip code'), we inject that as a match distance of 0.012. This gets
  around the fact that we often use 'zip code' when partners just use 'zip'."
  [db-fields header]
  (map (fn [{:keys [name text]}]
         (let [lc-header (str/lower-case header)
               lc-text (str/lower-case text)
               lc-header-words (str/split lc-header #"\s+")
               lc-header-word-set (set lc-header-words)
               lc-text-words (str/split lc-text #"\s+")
               lc-text-word-set (set lc-text-words)
               md (cond
                    (str/includes? lc-header
                                   lc-text)
                    0.01

                    (every? lc-header-word-set lc-text-words)
                    0.011

                    (and (lc-text-word-set "zip")
                         (lc-text-word-set "code")
                         (every? (conj lc-header-word-set "code")
                                 lc-text-words))
                    0.012

                    ;; Special-case to avoid problems with large files and
                    ;; auto-selecting year of birth
                    (= :dob name)
                    1

                    :else
                    (fuzzy-metrics/jaccard lc-header lc-text))]
           [md header name]))
       db-fields))

(defn header-match-distances
  "Takes a collection of string `headers` from a partner import file,
  calculates header match distances using `header-match-distance`, and returns
  a vector of the vectors returned by that function, sorted by their match
  distances (ascending; so better matches to worse matches)."
  [standard-fields headers]
  (->> headers
       (mapcat (partial header-match-distance standard-fields))
       (sort-by first)
       vec))

(defn default-header-mappings
  "Takes a collection of string `headers` from a partner import file and
  determines the best default Ballot Scout field to map them to. This is
  intended for the UI to show as the initial field mapping but still allowing
  the user to change it if it's not right. It uses `header-match-distances`
  to determine the best default matches, but sets a match distance cutoff of
  0.5 (after which a field will just be mapped to '-- do not import --' in the
  field mapping UI).

  Returns a map of partner import header strings to Ballot Scout field name
  keywords. This is intended for use in the app state's :header-mappings
  value."
  [standard-fields headers]
  (let [distances (header-match-distances standard-fields headers)]
    (loop [d (first distances)
           ds (rest distances)
           db-fields standard-fields
           mappings {}]
      (let [unmapped-field-set (->> db-fields
                                    (map :name)
                                    set)
            jaccard-distance (first d)
            header (second d)
            field (nth d 2)
            match (when (and (< jaccard-distance 0.5)
                             (unmapped-field-set field)
                             (not (contains? mappings header)))
                    [header field])]
        (if (seq ds)
          (recur (first ds)
                 (rest ds)
                 (remove #(= (:name %) (second match)) db-fields)
                 (conj mappings match))
          (merge
           (into {} (map (fn [h] [h not-imported]) headers))
           (conj mappings match)))))))

(defn column-selector
  "Component for choosing which internal field should be mapped to the given header"
  [standard-fields header-mapping header]
  [:select {:id :column-selector
            :value (get @header-mapping header)
            :on-change (fn [e]
                         (let [value (.. e -target -value)
                               value (if (= not-imported value)
                                       value
                                       (keyword value))]
                           (swap! header-mapping assoc header value)))}
   [:option {:value not-imported} not-imported]
   (for [field standard-fields]
     [:option (merge {:value (name (:name field)) :key (str "option-" (:name field))}
                     (when true
                       {:selected true}))
      (if (:required field)
        (:text field)
        (str (:text field) " (optional)"))])])

(defn missing-fields
  [standard-fields header-mappings]
  (let [chosen-fields (-> header-mappings vals set)]
    (->> chosen-fields
         (required-fields standard-fields)
         (remove #(chosen-fields (:name %)))
         set)))

(defn duplicated-fields
  [standard-fields header-mappings]
  (let [duplicated-names (for [[name count] (frequencies (vals header-mappings))
                               :when (> count 1)]
                           name)]
    (set (filter #((set duplicated-names) (:name %)) standard-fields))))

(defn error-fields
  [fields title]
  (when-not (empty? fields)
    [:div {:class "notification is-danger is-light"}
     [:h5 {:class "subtitle"}
      [:span {:class "has-text-weight-bold"} "Error: "]
      title]
     [:ul
      (for [{:keys [text]} fields]
        [:li text])]]))

(defn name-warning [header-mapping]
  (let [fields-in-mapping (set (vals header-mapping))]
    (when (and (fields-in-mapping :fullname)
               (or (fields-in-mapping :firstname)
                   (fields-in-mapping :lastname)
                   (fields-in-mapping :middlename)))
      [:div {:class "notification is-warning is-light"}
       [:h5 {:class "subtitle"}
        [:span {:class "has-text-weight-bold"} "Warning: "]
        "Redundant name fields"]
       "A Full Name field was specified in addition to other name fields. This may produce a conflict with name data."])))

#_:clj-kondo/ignore
(defn field-matcher [standard-fields headers setting-column-mapping? submit-mapping-fn]
  (let [header-mapping (r/atom (default-header-mappings standard-fields headers))]
    (fn [standard-fields headers setting-column-mapping? submit-mapping-fn]
      (let [missing (missing-fields standard-fields @header-mapping)
            duplicates (duplicated-fields standard-fields @header-mapping)
            valid? (and (empty? missing) (empty? duplicates))]
        [:div {:class "columns"}
         [:div {:class "column is-8"}
          [:table {:class "table is-fullwidth"}
           [:thead
            [:tr
             [:th "Your file's fields"]
             [:th "Ballot Scout fields"]]]
           [:tbody
            (for [header headers]
              [:tr {:key (str "column-" header)}
               [:td header]
               [:td [column-selector standard-fields header-mapping header]]])]]
          (when-not (empty? missing)
            [error-fields missing "Missing Fields"])
          (when-not (empty? duplicates)
            [error-fields duplicates "Duplicated Fields"])
          [name-warning @header-mapping]
          [:button (merge {:class "button is-primary"}
                          (if (or (not valid?) setting-column-mapping?)
                            {:disabled true}
                            {:on-click #(submit-mapping-fn @header-mapping)}))
           [:span "Confirm Headers"]
           [:span {:class "icon is-small"}
            [icon (if setting-column-mapping? ::icon/spinner ::icon/chevron-right)
             {:pulse? setting-column-mapping?}]]]]]))))
